import React, { useMemo, useState } from "react";
import CustomTable from "components/Table/CustomTable";
import { useModal } from "contexts/ModalProvider";
import { Dropdown, DropdownMenu, DropdownToggle, DropdownItem } from "reactstrap";
import { ReactComponent as EditIcon } from "assets/icons/edit.svg";
import { ReactComponent as TrashIcon } from "assets/icons/trash.svg";
import { AdminsSchema } from "model/admins/admins.schema";
import { useToasts } from "utils/useHotToast";
import ToastNotification from "components/Toast/ToastNotification";
import check from "assets/icons/check-circle.svg";
import slash from "assets/icons/slash-circle.svg";
import { useDeleteAdminMutation } from "services/admins/api";
import StatusBadge from "components/Common/Badge/StatusBadge";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

// Custom Hooks
import useTableChangeHandler from "hooks/useTableChangeHandler";

const AdminTable = ({ data, onTableChange, isLoading, error }) => {
  const { modal } = useModal();
  const { addToast, removeToast } = useToasts();
  const [actionsDropdown, setActionsDropdown] = useState({});
  const [selectedAdmin, setSelectedAdmin] = useState([]);
  const [deleteAdmin] = useDeleteAdminMutation();
  const navigate = useNavigate();
  const currentUserEmail = useSelector((state) => state.profile.user?.email);

  // HOOK CHANGE
  const { handleSort, handleFilter, handleSearch, handlePageChange } = useTableChangeHandler(onTableChange);

  const columns = useMemo(
    () => [
      {
        header: "แอดมินผู้ใช้งาน",
        accessor: "adminName",
        sortable: true,
        render: (item) => (
          <div>
            <div className="text-lg fw-semibold">{`${item.firstName} ${item.lastName}`}</div>
            <div className="text-md text-gray-500">{item.email}</div>
          </div>
        ),
      },
      {
        header: "วันที่สมัครสมาชิก",
        accessor: "registeredAt",
        align: "center",
        responsive: "md",
        render: (item) => dayjs(item.createdAt).format("YYYY-MM-DD"),
      },
      {
        header: "สถานะยืนยันตัวตน",
        accessor: "isValidated",
        width: 160,
        render: (item) => <span>{item.isVerified ? <StatusBadge variant="success">ยืนยันตัวตนแล้ว</StatusBadge> : <StatusBadge variant="warning">ยังไม่ได้ยืนยันตัว</StatusBadge>}</span>,
      },
      {
        header: "",
        accessor: "actions",
        width: 50,
        render: (item) => (
          <Dropdown
            size="thin"
            isOpen={actionsDropdown[item.id]}
            toggle={() =>
              setActionsDropdown({
                ...actionsDropdown,
                [item.id]: !actionsDropdown[item.id],
              })
            }
          >
            <DropdownToggle tag="span" className="cursor-pointer">
              <i className="mdi mdi-dots-vertical" />
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem onClick={() => navigate(`/admin/form/${item.id}`)}>
                <EditIcon />
                แก้ไขข้อมูล
              </DropdownItem>
              {item.email !== currentUserEmail && (
                <DropdownItem onClick={() => handleDelete(item)} disabled={item.email === currentUserEmail} title={item.email === currentUserEmail ? "ไม่สามารถลบบัญชีของตัวเองได้" : ""}>
                  <TrashIcon />
                  ลบแอดมิน
                </DropdownItem>
              )}
            </DropdownMenu>
          </Dropdown>
        ),
      },
    ],
    [actionsDropdown, setActionsDropdown, data]
  );

  const renderUser = (item) => (
    <div>
      <div className="text-lg fw-semibold">{`${item.firstName} ${item.lastName}`}</div>
      <div className="text-md text-gray-500">{item.email}</div>
    </div>
  );

  const handleDelete = async (item) => {
    const result = await modal.alert.danger({
      title: "ยืนยันที่จะลบแอดมินออกจากระบบ",
      message: `ตรวจสอบรายชื่อแอดมินที่คุณต้องการลบออกจากระบบว่าถูกต้องหรือไม่? เมื่อลบแล้ว แอดมินที่ถูกลบจะไม่สามารถเข้าสู่ระบบได้อีกต่อไป`,
      confirmText: "ยืนยันที่จะลบ",
      cancelText: "ยกเลิก",
      children: renderUser(item),
    });
    if (result) {
      try {
        await deleteAdmin(item.id);
        const toastId = addToast(<ToastNotification variant="success" icon={check} title="ลบแอดมินออกจากระบบแล้ว" description={`แอดมิน "${item.firstName} ${item.lastName}" ถูกลบออกจากระบบเรียบร้อยแล้ว`} onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
        // Refresh data
        if (onTableChange) {
          onTableChange({});
        }
      } catch (error) {
        console.error("Failed to delete admin:", error);
        const toastId = addToast(<ToastNotification variant="danger" icon={slash} title="เกิดข้อผิดพลาด" description="ไม่สามารถลบแอดมินออกจากระบบได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
      }
    }
  };

  const handleDeleteSelected = async () => {
    console.log(selectedAdmin);
    const result = await modal.alert.danger({
      title: "ยืนยันที่จะลบแอดมินออกจากระบบ",
      message: `คุณได้ทำการกดลบแอดมินออกจากระบบทั้งหมด "${selectedAdmin.length} รายการ" เมื่อลบแล้ว แอดมินที่ถูกลบจะไม่สามารถเข้าสู่ระบบได้อีกต่อไป`,
      confirmText: "ยืนยันที่จะลบ",
      cancelText: "ยกเลิก",
      children: (
        <div className="d-flex flex-column gap-3 divide-y">
          {selectedAdmin.map((item, index) => (
            <div key={item.id} className={`d-flex flex-column gap-2 ${index > 0 && "pt-3"}`}>
              <div>
                <div className="text-lg fw-semibold">{`${item.firstName} ${item.lastName}`}</div>
                <div className="text-md text-gray-500">{item.email}</div>
              </div>
            </div>
          ))}
        </div>
      ),
    });
    if (result) {
      try {
        await deleteAdmin(selectedAdmin.map((item) => item.id));
        const toastId = addToast(<ToastNotification variant="success" icon={check} title={`ลบแอดมินออกจากระบบแล้ว ${selectedAdmin.length} รายการ`} description="แอดมินที่เลือกถูกลบออกจากระบบเรียบร้อยแล้ว" onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
        // Refresh data
        if (onTableChange) {
          onTableChange({});
        }
        // Clear selection
        setSelectedAdmin([]);
      } catch (error) {
        console.error("Failed to delete admins:", error);
        const toastId = addToast(<ToastNotification variant="danger" icon={slash} title="เกิดข้อผิดพลาด" description="ไม่สามารถลบแอดมินออกจากระบบได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
      }
    }
  };

  const handleSelectionChange = (selectedIds) => {
    setSelectedAdmin(selectedIds);
  };

  return (
    <div data-testid="admin-table">
      <div className="text-lg mb-4">รายการแอดมินที่มีในระบบทั้งหมด * {data?.pagination?.total?.toLocaleString() || 0} รายการ *</div>

      <CustomTable
        keyField="id"
        data={data?.data || []}
        columns={columns}
        selectable={true}
        onSelectionChange={handleSelectionChange}
        selectionActions={
          <a className="danger" onClick={handleDeleteSelected}>
            <i className="bx bx-trash" />
            <span className="d-none d-md-block">ลบรายการที่เลือกไว้</span>
          </a>
        }
        className="commission-table"
        itemsPerPage={data?.pagination?.limit || 10}
        totalItems={data?.pagination?.total || 0}
        onSort={handleSort}
        onFilter={handleFilter}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        status={isLoading ? "loading" : error ? "error" : data?.data?.length === 0 ? "empty" : "normal"}
        errorMessage={error?.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล"}
        emptyMessage="ไม่พบข้อมูลแอดมิน"
      />
    </div>
  );
};

const withYupValidation = (WrappedComponent, schema) => {
  return function WithYupValidation(props) {
    // Skip validation if loading or error
    if (props.isLoading || props.error) {
      return <WrappedComponent {...props} />;
    }

    try {
      schema.validateSync(props.data);
      return <WrappedComponent {...props} />;
    } catch (error) {
      return (
        <div className="alert alert-danger">
          <strong>Validation Error:</strong> {error.message}
        </div>
      );
    }
  };
};

const ValidatedTable = withYupValidation(AdminTable, AdminsSchema);

export default ValidatedTable;
